import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AboutSection from "../components/About";
import Gallery from "../components/Gallery";
import UpcomingEvents from '../components/UpcomingEvents';
import { scrollToTop } from "../utils/scrollToTop";


const HomePage = () => {
  const navigate = useNavigate();
  
  // Language toggle state - starts with English
  const [isHindi, setIsHindi] = useState(false);
  
  // Auto-switch language every 5 seconds with fade effect
  useEffect(() => {
    const interval = setInterval(() => {
      setIsHindi(prev => !prev);
    }, 5000); // 5 seconds interval
    
    return () => clearInterval(interval);
  }, []);
  
  // Hero content in both languages
  const heroContent = {
    english: {
      title1: "KABIR NAGAR",
      title2: "KHEL SAMITI SOCIETY",
      location: "RAIPUR • CHHATTISGARH",
      tagline: "Where Champions Are Born • Where Dreams Take Flight",
      description: "Empowering Youth Through Sports • Building Tomorrow's Leaders"
    },
    hindi: {
      title1: "कबीर नगर",
      title2: "खेल समिति सोसाइटी",
      location: "रायपुर • छत्तीसगढ़",
      tagline: "जहाँ चैंपियन पैदा होते हैं • जहाँ सपने उड़ान भरते हैं",
      description: "खेल के माध्यम से युवाओं को सशक्त बनाना • कल के नेताओं का निर्माण"
    }
  };
  
  const currentContent = isHindi ? heroContent.hindi : heroContent.english;

  const handleRegisterClick = () => {
    navigate("/register");
    scrollToTop();
  };

  const handleSeeMore = (route) => {
    navigate(route);
    scrollToTop();
  };

  return (
    <main className="w-full">
      {/* Hero Section - BigCricketLeague Inspired */}
      <section className="relative w-full h-screen overflow-hidden">
        {/* Background Video/Image Container */}
        <div 
          className="absolute inset-0 scale-105"
          style={{
            backgroundImage: "url('/images/heroSection_img.jpg')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}  
          
        />

        {/* Professional Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 via-blue-800/70 to-slate-900/80 z-10" />

        {/* Content Container */}
        <div className="absolute inset-0 z-20">
          <div className="h-full flex items-center justify-center">
            <div className="text-center w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
               <div className="space-y-6 md:space-y-8">
                {/* Animated Title with Language Switch */}
                <h1 
                  className="hero-title drop-shadow-2xl transition-opacity duration-1000"
                  style={{ opacity: 1 }}
                  key={isHindi ? 'hindi' : 'english'}
                >
                  <span className="block text-white animate-fadeIn">
                    {currentContent.title1}
                  </span>
                  <span className="block text-orange-400 animate-fadeIn">
                    {currentContent.title2}
                  </span>
                  <span className="block text-sm sm:text-base md:text-lg lg:text-xl font-medium tracking-wider text-gray-200 mt-2 animate-fadeIn">
                    {currentContent.location}
                  </span>
                </h1>
                
                {/* Enhanced Taglines with Fade Animation */}
                <div className="space-y-4">
                  <p 
                    className="hero-subtitle max-w-4xl mx-auto drop-shadow-lg transition-all duration-1000 animate-fadeIn"
                    key={`tagline-${isHindi ? 'hindi' : 'english'}`}
                  >
                    {currentContent.tagline}
                  </p>
                  <p 
                    className="hero-description max-w-3xl mx-auto drop-shadow-lg transition-all duration-1000 animate-fadeIn"
                    key={`description-${isHindi ? 'hindi' : 'english'}`}
                  >
                    {currentContent.description}
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center mt-8">
                  <button
                    onClick={handleRegisterClick}
                    className="bg-gradient-to-r from-orange-500 to-red-600 text-white
                             px-8 sm:px-10 md:px-12
                             py-4 sm:py-5
                             btn-text-xl
                             rounded-xl
                             transition-all duration-300
                             hover:from-orange-600 hover:to-red-700
                             hover:scale-105 hover:shadow-2xl
                             border-2 border-white/20
                             backdrop-blur-sm
                             shadow-xl"
                  >
                    JOIN THE LEAGUE
                  </button>
                  
                  <button
                    onClick={() => handleSeeMore('/about')}
                    className="bg-transparent text-white
                             px-8 sm:px-10 md:px-12
                             py-4 sm:py-5
                             btn-text-xl
                             rounded-xl
                             transition-all duration-300
                             hover:bg-white/10
                             hover:scale-105
                             border-2 border-white/60
                             backdrop-blur-sm
                             shadow-xl"
                  >
                    DISCOVER MORE
                  </button>
                </div>

                {/* Enhanced Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-8 mt-12 sm:mt-16 max-w-5xl mx-auto">
                  <div className="text-center">
                    <div className="stat-number">300+</div>
                    <div className="stat-label">Active Players</div>
                  </div>
                  <div className="text-center">
                    <div className="stat-number">25+</div>
                    <div className="stat-label">Tournaments</div>
                  </div>
                  <div className="text-center">
                    <div className="stat-number">8+</div>
                    <div className="stat-label">Years Legacy</div>
                  </div>
                  <div className="text-center">
                    <div className="stat-number">15+</div>
                    <div className="stat-label">Members</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
          <div className="animate-bounce">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <div className="max-w-7xl mx-auto px-8">
        
        {/* Why Choose KNKSS Section */}
        <section className="w-full pt-16 pb-20">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
              Why Choose <span className="text-orange-600">KNKSS</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-red-600 mx-auto mb-4"></div>
            <p className="section-subtitle max-w-3xl mx-auto">
              Discover what makes us the premier choice for sports development in Raipur
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100">
              <div className="text-orange-600 mb-6">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-h4 text-gray-900 mb-4">
                Community Focus
              </h3>
              <p className="text-body text-gray-600">
                Building strong bonds and lasting friendships that unite people of all ages and backgrounds in Kabir Nagar.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100">
              <div className="text-orange-600 mb-6">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-h4 text-gray-900 mb-4">
                Future Champions
              </h3>
              <p className="text-body text-gray-600">
                Nurturing talent and inspiring healthy, active lifestyles to create tomorrow's sporting leaders and champions.
              </p>
            </div>
          </div>
        </section>

        {/* Registration Fees Section */}
        <section className="w-full py-20 bg-gradient-to-br from-blue-50 to-orange-50">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
              Tournament <span className="text-orange-600">Registration Fees</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-red-600 mx-auto mb-4"></div>
            <p className="section-subtitle max-w-3xl mx-auto">
              For organizing sports activities, every tournament collection of per players registration fees
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Old Players */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100">
              <div className="text-center">
                <div className="text-orange-600 mb-6">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <h3 className="text-h3 text-gray-900 mb-4">
                  Old Players
                </h3>
                <div className="text-4xl font-bold text-orange-600 mb-4">
                  ₹800/-
                </div>
                <p className="text-body text-gray-600">
                  Registration fees for existing members who have participated in previous tournaments
                </p>
                <div className="mt-6">
                  <span className="inline-block bg-green-100 text-green-800 text-sm font-medium px-3 py-1 rounded-full">
                    Special Rate
                  </span>
                </div>
              </div>
            </div>
            
            {/* New Players */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100">
              <div className="text-center">
                <div className="text-orange-600 mb-6">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                </div>
                <h3 className="text-h3 text-gray-900 mb-4">
                  New Players
                </h3>
                <div className="text-4xl font-bold text-orange-600 mb-4">
                  ₹1,200/-
                </div>
                <p className="text-body text-gray-600">
                  Registration fees for new members joining tournaments for the first time
                </p>
                <div className="mt-6">
                  <span className="inline-block bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full">
                    Welcome Rate
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section className="w-full pt-12 pb-20">
          <AboutSection />
        </section>

        {/* Our Programs Section */}
        <section className="w-full pt-12 pb-20 bg-gradient-to-br from-gray-50 via-white to-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
                Our <span className="text-orange-600">Programs</span>
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-red-600 mx-auto mb-4"></div>
              <p className="section-subtitle max-w-3xl mx-auto">
                Comprehensive sports development programs designed for every skill level and age group
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C20.832 18.477 19.246 18 17.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h3 className="card-title text-gray-900 mb-2">Youth Training</h3>
                <p className="card-body">Fundamental skills training for young athletes aged 8-16</p>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="card-title text-gray-900 mb-2">Community Leagues</h3>
                <p className="card-body">Local tournaments and friendly matches for all skill levels</p>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className="card-title text-gray-900 mb-2">Fitness Programs</h3>
                <p className="card-body">Physical conditioning and wellness programs for overall health</p>
              </div>
            </div>
          </div>
        </section>

        {/* Gallery Section */}
        <section className="w-full py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Gallery />
          </div>
        </section>

        {/* Upcoming Events Section */}
        <section className="w-full py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <UpcomingEvents isHomepage={true} />
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="w-full py-12">
          <div className="bg-gradient-to-r from-orange-500 to-red-600 rounded-2xl p-12 text-white text-center">
            <h3 className="text-h3 mb-4">
              Ready to Start Your Journey?
            </h3>
            <p className="text-body mb-8 opacity-90 max-w-2xl mx-auto">
              Join thousands of athletes who have discovered their potential with KNKSS. 
              Your sporting journey begins with a single step.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={handleRegisterClick}
                className="bg-white text-orange-600 px-8 py-4 rounded-xl btn-text-lg hover:bg-gray-100 transition-all duration-300 hover:scale-105 shadow-lg"
              >
                Register Now
              </button>
              <button 
                onClick={() => handleSeeMore('/about')}
                className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl btn-text-lg hover:bg-white hover:text-orange-600 transition-all duration-300 hover:scale-105"
              >
                Learn More
              </button>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
};

export default HomePage;